import { Box, Tooltip, Typography } from "@mui/material";
import strings from "global/constants/StringConstants";
import { fillCopy } from "helpers/methods";
import { getIdvBadge } from "helpers/identityVerificationHelpers";
import { SignerIdvSummary } from "types/identityVerification";
import { formatAuditDateTime } from "./ViewRequestStatusMeta";
import viewSignRequestStyles from "./ViewSignRequest.styles";

const styles = viewSignRequestStyles;
const copy = strings.identityVerification;

/** One line per method: "Photo ID and selfie: Passed · 1 of 3 attempts · Oct 7, 2026 · 2:03 PM". */
const methodLine = (
  method: SignerIdvSummary["methods"][number],
  maxAttempts: number,
) =>
  [
    `${copy.methods[method.method]?.label ?? method.method}: ${
      copy.methodStatuses[method.status] ?? method.status
    }`,
    fillCopy(copy.attemptsUsed, { used: method.attempts, max: maxAttempts }),
    formatAuditDateTime(method.completedOn ?? undefined),
  ]
    .filter(Boolean)
    .join(" · ");

/**
 * Per-signer identity verification badge on the request detail. Reuses the
 * screen's own status-chip chrome (gray / blue / amber / red / green) with the
 * label always in text, and lists each method's result in its tooltip.
 * `NOT_REQUIRED` renders nothing.
 */
const IdentityVerificationBadge = ({
  summary,
}: {
  summary?: SignerIdvSummary;
}) => {
  const badge = getIdvBadge(summary?.status);
  if (!summary || !badge) return null;

  const details = (summary.methods ?? []).map((method) => (
    <Typography key={method.method} component="div" variant="caption">
      {methodLine(method, summary.maxAttempts)}
    </Typography>
  ));

  return (
    <Tooltip
      title={details.length ? <Box>{details}</Box> : ""}
      disableHoverListener={!details.length}
      disableFocusListener={!details.length}
      arrow
      describeChild
    >
      <Box
        tabIndex={details.length ? 0 : undefined}
        role="group"
        aria-label={fillCopy(copy.badgeAriaLabel, { status: badge.label })}
        sx={styles.statusChip(badge.tone, true)}
      >
        <Box component="span" sx={styles.statusDot(badge.tone, true)} />
        <Box component="span" sx={styles.statusLabel(badge.tone, true)}>
          {badge.label}
        </Box>
      </Box>
    </Tooltip>
  );
};

export default IdentityVerificationBadge;
